import { FC, useContext, useEffect, useMemo, useState } from "react";
import { QesActiveFiltersBar } from "@app/shared/QesActiveFiltersBar";
import type { FilterChipDescriptor } from "@app/shared/QesActiveFiltersBar";
import {
  networkPortfolioManagementProjectStatusDisplayLabel,
  networkPortfolioManagementProjectTypeDisplayLabel,
  projectActionDisplayLabel,
  selectableProjectTypes,
} from "@app/network-portfolio-management/interfaces";
import { emptyActiveFilters } from "@app/network-portfolio-management/filterStorage";
import { useFilterContext } from "@app/network-portfolio-management/contexts/FilterContext";
import { useFilterOptions } from "@app/network-portfolio-management/hooks/useFilterOptions";
import {
  countyChipLabel,
  countyKey,
  lobChipLabel,
  parseSpecialtyOptionValue,
  projectStatusChipLabel,
  projectTypeChipLabel,
  pucChipLabel,
  specialtyChipLabel,
  specialtyGroupItemKey,
  specialtyOptionLabel,
  stateChipLabel,
  templateChipLabel,
} from "@app/network-portfolio-management/components/filterBarHelpers";
import { tagChipLabel } from "@app/network-portfolio-management/components/tagChipLabel";
import { SaveAsViewModal } from "@app/network-portfolio-management/components/SaveAsViewModal";
import { useSaveViewHandler } from "@app/network-portfolio-management/hooks/useSaveViewHandler";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QESButton } from "@app/shared/qes-standards/Button";

export const ActiveFiltersBar: FC = () => {
  const { activeFilters, activeSavedViewId, loading, setActiveFilters } = useFilterContext();
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const savedViewsEnabled = enabledFeatureFlags.has(Features.PROJECT_ARGUS_PROJECT_DASHBOARD_SAVED_VIEWS);
  const { filterOptions, filterOptionsError } = useFilterOptions();

  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const handleSaved = useSaveViewHandler();

  // The flag can flip off mid-session while the modal is open, which unmounts it without onClose firing.
  // Drop the open state too, so the modal doesn't reappear on its own if the flag comes back.
  useEffect(() => {
    if (!savedViewsEnabled) setSaveModalOpen(false);
  }, [savedViewsEnabled]);

  const handleClearAll = () => {
    // emptyActiveFilters() already clears projectAction (to null) along with the rest.
    setActiveFilters(emptyActiveFilters());
  };

  const chips = useMemo<FilterChipDescriptor[]>(() => {
    const result: FilterChipDescriptor[] = [];

    // Project Type is the first filter, so its chips lead the bar. Options are UI-defined, so labels come from
    // the canonical project-type display map; dismissing clears only that project type.
    const projectTypeNameByValue = new Map(
      selectableProjectTypes.map((type) => [type, networkPortfolioManagementProjectTypeDisplayLabel[type] ?? type]),
    );

    for (const value of activeFilters.projectTypes) {
      const label = projectTypeChipLabel(projectTypeNameByValue.get(value) ?? value);
      result.push({
        ariaLabel: `Remove filter ${label}`,
        content: label,
        iconSize: "10",
        key: `project-type-${value}`,
        onDismiss: () => setActiveFilters((prev) => ({
          ...prev,
          projectTypes: prev.projectTypes.filter((x) => x !== value),
        })),
      });
    }

    // The Project Action filter (role-restricted; coerced to null for users without the role upstream in
    // FilterContext) is surfaced as a chip so it's visible and individually removable. Dismissing (or Clear All)
    // clears it.
    if (activeFilters.projectAction !== null) {
      const label = projectActionDisplayLabel[activeFilters.projectAction];
      result.push({
        ariaLabel: `Remove filter ${label}`,
        content: label,
        iconSize: "10",
        key: "project-action",
        onDismiss: () => setActiveFilters((prev) => ({ ...prev, projectAction: null })),
      });
    }

    const lobNameById = filterOptions
      ? new Map(filterOptions.linesOfBusiness.map((o) => [Number(o.value), o.displayName]))
      : null;

    if (lobNameById || filterOptionsError) {
      for (const id of activeFilters.linesOfBusinessIds) {
        const label = lobChipLabel(lobNameById?.get(id) ?? String(id));
        result.push({
          ariaLabel: `Remove filter ${label}`,
          content: label,
          iconSize: "10",
          key: `lob-${id}`,
          onDismiss: () => setActiveFilters((prev) => ({
            ...prev,
            linesOfBusinessIds: prev.linesOfBusinessIds.filter((x) => x !== id),
          })),
        });
      }
    }

    // Project Utilization chips (role-restricted; coerced to empty for users without the role upstream in
    // FilterContext, so no chips render for them). Dismissing one clears only that utilization value.
    const pucNameById = filterOptions
      ? new Map(filterOptions.projectUtilizations.map((o) => [Number(o.value), o.displayName]))
      : null;

    if (pucNameById || filterOptionsError) {
      for (const id of activeFilters.projectUtilizationCategoryIds) {
        const label = pucChipLabel(pucNameById?.get(id) ?? String(id));
        result.push({
          ariaLabel: `Remove filter ${label}`,
          content: label,
          iconSize: "10",
          key: `puc-${id}`,
          onDismiss: () => setActiveFilters((prev) => ({
            ...prev,
            projectUtilizationCategoryIds: prev.projectUtilizationCategoryIds.filter((x) => x !== id),
          })),
        });
      }
    }

    // State chips - fully-selected states stored explicitly; no filterOptions needed.
    for (const stateAbbrev of activeFilters.serviceAreaStates) {
      result.push({
        ariaLabel: `Remove filter ${stateChipLabel(stateAbbrev)}`,
        content: stateChipLabel(stateAbbrev),
        iconSize: "10",
        key: `state-${stateAbbrev}`,
        onDismiss: () => setActiveFilters((prev) => ({
          ...prev,
          serviceAreaStates: prev.serviceAreaStates.filter((s) => s !== stateAbbrev),
        })),
      });
    }

    // County chips - partial selections; name resolution requires filterOptions.
    const countyNameMap = filterOptions
      ? new Map(
          filterOptions.serviceAreas.flatMap((state) =>
            (state.children ?? []).map((county) => [
              countyKey(state.value, county.value),
              { countyName: county.displayName, stateName: state.value },
            ])
          )
        )
      : null;

    if (countyNameMap || filterOptionsError) {
      for (const c of activeFilters.serviceAreaCounties) {
        const resolved = countyNameMap?.get(countyKey(c.state, c.county));
        const displayRef = { county: resolved?.countyName ?? c.county, state: resolved?.stateName ?? c.state };
        const label = countyChipLabel(displayRef);
        result.push({
          ariaLabel: `Remove filter ${label}`,
          content: label,
          iconSize: "10",
          key: `county-${c.state}-${c.county}`,
          onDismiss: () => setActiveFilters((prev) => ({
            ...prev,
            serviceAreaCounties: prev.serviceAreaCounties.filter(
              (x) => !(x.state === c.state && x.county === c.county),
            ),
          })),
        });
      }
    }

    // Keyed by the client-side group identity so it matches the keys built from activeFilters below.
    const specialtyNameByItemKey = filterOptions
      ? new Map(filterOptions.specialties.flatMap((o) => {
        const group = parseSpecialtyOptionValue(o.value);
        return group ? [[specialtyGroupItemKey(group), o.displayName] as const] : [];
      }))
      : null;

    if (specialtyNameByItemKey || filterOptionsError) {
      for (const s of activeFilters.specialtyGroups) {
        const key = specialtyGroupItemKey(s);
        const label = specialtyChipLabel(specialtyNameByItemKey?.get(key) ?? specialtyOptionLabel(s.code, s.name));
        result.push({
          ariaLabel: `Remove filter ${label}`,
          content: label,
          iconSize: "10",
          key: `specialty-${key}`,
          onDismiss: () => setActiveFilters((prev) => ({
            ...prev,
            specialtyGroups: prev.specialtyGroups.filter((x) => !(x.code === s.code && x.name === s.name)),
          })),
        });
      }
    }

    const templateNameByValue = filterOptions
      ? new Map(filterOptions.templates.map((o) => [o.value, o.displayName]))
      : null;

    if (templateNameByValue || filterOptionsError) {
      for (const t of activeFilters.templateNames) {
        const label = templateChipLabel(templateNameByValue?.get(t) ?? t);
        result.push({
          ariaLabel: `Remove filter ${label}`,
          content: label,
          iconSize: "10",
          key: `template-${t}`,
          onDismiss: () => setActiveFilters((prev) => ({
            ...prev,
            templateNames: prev.templateNames.filter((x) => x !== t),
          })),
        });
      }
    }

    const tagDisplayById = filterOptions
      ? new Map(
          filterOptions.tags.flatMap((group) =>
            (group.children ?? []).map((tag) => [
              Number(tag.value),
              { group: group.displayName, tag: tag.displayName },
            ])
          )
        )
      : null;

    if (tagDisplayById || filterOptionsError) {
      for (const id of activeFilters.tagIds) {
        const display = tagDisplayById?.get(id) ?? { group: String(id), tag: "" };
        result.push({
          ariaLabel: `Remove filter ${display.group} >> ${display.tag}`,
          content: tagChipLabel(display),
          iconSize: "10",
          key: `tag-${id}`,
          onDismiss: () => setActiveFilters((prev) => ({
            ...prev,
            tagIds: prev.tagIds.filter((x) => x !== id),
          })),
        });
      }
    }

    // Project Status chips - one per selected status; the label comes from the UI-owned status map. Preview-gated
    // values a user can't see are already stripped upstream (FilterContext).
    for (const status of activeFilters.projectStatuses) {
      const label = projectStatusChipLabel(networkPortfolioManagementProjectStatusDisplayLabel[status]);
      result.push({
        ariaLabel: `Remove filter ${label}`,
        content: label,
        iconSize: "10",
        key: `project-status-${status}`,
        onDismiss: () => setActiveFilters((prev) => ({
          ...prev,
          projectStatuses: prev.projectStatuses.filter((x) => x !== status),
        })),
      });
    }

    if (activeFilters.searchTerm.trim().length > 0) {
      result.push({
        ariaLabel: `Remove search filter ${activeFilters.searchTerm}`,
        content: `Search: ${activeFilters.searchTerm}`,
        iconSize: "10",
        key: "search-term",
        onDismiss: () => setActiveFilters((prev) => ({ ...prev, searchTerm: "" })),
      });
    }

    return result;
  }, [activeFilters, filterOptions, filterOptionsError, setActiveFilters]);

  const showSaveCta = savedViewsEnabled && chips.length > 0 && activeSavedViewId === null && !loading;

  const saveCta = showSaveCta ? (
    <QESButton
      qesType="none"
      qesTestId="npm-active-filters-save-view"
      className="network-portfolio-management-active-filters-bar__save-link"
      onClick={() => setSaveModalOpen(true)}
    >
      <QesIcon iconName={QesIcons.BookmarkAdd} isDecorative className="network-portfolio-management-active-filters-bar__save-link-icon" />
      Save as view
    </QESButton>
  ) : undefined;

  return (
    <>
      <QesActiveFiltersBar
        chips={chips}
        collapsedRows={3}
        maxRows={6}
        onClearAll={chips.length > 0 ? handleClearAll : undefined}
        testIdPrefix="npm-active-filter"
        trailingContent={saveCta}
        variant="flat"
      />
      {savedViewsEnabled && (
        <SaveAsViewModal
          open={saveModalOpen}
          onClose={() => setSaveModalOpen(false)}
          onSaved={handleSaved}
        />
      )}
    </>
  );
};
